<template>
    <BaseMockBookingForm :coverImageUrl="coverImageUrl">
        <template #addressBar>
            <div
                class="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600"
            >
                <div
                    class="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"
                >
                    <Icon
                        name="globe"
                        class="h-2.5 w-2.5"
                    />
                </div>
                <span class="truncate font-medium">
                    {{ bookingPageUrl }}
                </span>
            </div>
        </template>

        <template #content>
            <div class="bg-white">
                <div
                    class="grid border-t border-slate-200 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
                >
                    <div class="space-y-5 p-5 md:p-6">
                        <div class="flex items-center gap-3">
                            <Image
                                :src="profileImageUrl"
                                :alt="displayName"
                                class="h-10 w-10 rounded-full object-cover"
                            />
                            <div class="min-w-0">
                                <div
                                    class="truncate text-sm font-semibold text-slate-900"
                                >
                                    {{ displayName }}
                                </div>
                            </div>
                        </div>

                        <div class="space-y-2">
                            <div
                                class="text-lg font-bold leading-tight text-slate-900"
                            >
                                {{ serviceTitle }}
                            </div>
                            <p class="text-sm text-slate-600">
                                {{ serviceDescription }}
                            </p>
                        </div>

                        <div class="space-y-2 text-sm text-slate-700">
                            <div class="flex items-center gap-2.5">
                                <img
                                    :src="meetingLocationIconUrl"
                                    alt="Google Meet"
                                    class="h-4 w-4 rounded-sm"
                                />
                                <span>Google Meet</span>
                            </div>
                            <div class="flex items-center gap-2.5">
                                <Icon
                                    name="stopwatch"
                                    class="h-4 w-4 text-slate-400"
                                />
                                <span>120 minutes</span>
                            </div>
                            <div class="flex items-center gap-2.5">
                                <Icon
                                    name="globe"
                                    class="h-4 w-4 text-slate-400"
                                />
                                <span>{{ browserTimeZone }}</span>
                            </div>
                        </div>
                    </div>

                    <div
                        class="space-y-4 border-t border-slate-200 p-5 md:border-t-0 md:p-6"
                    >
                        <div
                            class="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
                        >
                            <span class="truncate">{{ browserTimeZone }}</span>
                            <Icon
                                name="angle-down"
                                class="h-3 w-3 text-slate-400"
                            />
                        </div>

                        <div
                            class="space-y-4 rounded-xl border border-slate-200 p-4"
                        >
                            <div
                                class="h-4 w-32 rounded-full bg-slate-200"
                            ></div>
                            <div class="grid grid-cols-7 gap-2">
                                <div
                                    v-for="(calendarDay, index) in calendarDays"
                                    :key="index"
                                    :class="getCalendarDayClasses(calendarDay)"
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </BaseMockBookingForm>
</template>

<script setup lang="ts">
    import Icon from '@/Shared/Component/Icon.vue'
    import Image from '@/Shared/Component/Image.vue'
    import BaseMockBookingForm from '@/Shared/Component/BaseMockBookingForm.vue'
    import useDate from '@/Shared/Hook/useDate'

    interface CalendarDay {
        isMuted: boolean
        isSelected: boolean
    }

    defineProps({
        bookingPageUrl: {
            type: String,
            required: true,
        },
        displayName: {
            type: String,
            required: true,
        },
        profileImageUrl: {
            type: String,
            required: true,
        },
        coverImageUrl: {
            type: String,
            default: null,
        },
        serviceDescription: {
            type: String,
            required: true,
        },
        serviceTitle: {
            type: String,
            required: true,
        },
    })

    const browserTimeZone = useDate().getBrowserTimeZone()
    const meetingLocationIconUrl = '/static/icons/providers/google-meet.svg'
    const calendarDays: CalendarDay[] = [
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: false, isSelected: false },
        { isMuted: false, isSelected: false },
        { isMuted: false, isSelected: true },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: false, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
        { isMuted: true, isSelected: false },
    ]

    const getCalendarDayClasses = (calendarDay: CalendarDay): string => {
        if (calendarDay.isSelected) {
            return 'h-9 rounded-lg border border-slate-300 bg-white shadow-xs'
        }

        if (calendarDay.isMuted) {
            return 'h-9 rounded-lg bg-slate-100'
        }

        return 'h-9 rounded-lg border border-slate-200 bg-white'
    }
</script>
